// Type augmentation for window (must be before usage)
declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

// Environment variable accessor that works in both dev and prod
export const getEnv = (key: string): string | undefined => {
  // ALWAYS try window.__ENV__ first (set by docker-entrypoint.sh in production)
  if (
    typeof window !== "undefined" &&
    window.__ENV__ &&
    window.__ENV__[key]
  ) {
    return window.__ENV__[key];
  }

  // Fallback to import.meta.env for local development
  if (import.meta.env && import.meta.env[key]) {
    return import.meta.env[key];
  }

  return undefined;
};

// Unified LB-fronted API origin. Empty string in dev => relative URLs
// (Vite proxy routes /v1/<service>/* to the right backend port). In prod,
// VITE_API_URL is set to the LB hostname (e.g. https://api.taskeng.ai).
// All callers use `${config.apiUrl}/v1/<service>/<path>`.
const apiUrl = getEnv("VITE_API_URL") ?? "";

// Web Vitals (B9 / PLAT-314): endpoint + sample rate. The init function in
// `lib/web-vitals.ts` is the no-op gate — empty endpoint or 0 rate disables.
const webVitalsEndpoint = getEnv("VITE_WEB_VITALS_ENDPOINT") || "";
const webVitalsSampleRateRaw = getEnv("VITE_WEB_VITALS_SAMPLE_RATE");
const webVitalsSampleRate = (() => {
  if (webVitalsSampleRateRaw === undefined) return 0;
  const n = Number(webVitalsSampleRateRaw);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
})();

export const config = {
  sentryDsn: getEnv("VITE_SENTRY_DSN") || "",
  webVitalsEndpoint,
  webVitalsSampleRate,
  webVitalsEnabled: webVitalsEndpoint.length > 0 && webVitalsSampleRate > 0,
  // Defense-in-depth UI gate for live voice interviews. The server capability
  // and a registered browser transport must also be present before any voice
  // control is rendered. Defaults off until the private API adapter ships.
  voiceInterviewsEnabled: getEnv("VITE_VOICE_INTERVIEWS_ENABLED") === "true",
  // UI gate for the alternate-brand preview toggle (BRAND_TOGGLE_PLAN).
  // Off everywhere by default; enabled per-environment via env var —
  // runtime-injected through env.js, so flipping it on in prod later is a
  // deploy-workflow env change, not a rebuild.
  brandPreviewEnabled: getEnv("VITE_BRAND_PREVIEW_ENABLED") === "true",
  apiUrl,
  // Per-service URLs retained as dormant fallbacks. Nothing in the runtime
  // hot path reads them; kept only to avoid breaking out-of-tree callers
  // or dev tooling that may reference them.
  runeServiceUrl: getEnv("VITE_RUNE_SERVICE_URL") || "http://localhost:8000",
  // Public MCP server URL surfaced in Settings. In cloud envs this is
  // mcp.<env>.taskeng.ai (LB-fronted); locally falls back to runeService.
  mcpServerUrl: getEnv("VITE_MCP_SERVER_URL") || "http://localhost:8000",
  structureServiceUrl:
    getEnv("VITE_STRUCTURE_SERVICE_URL") || "http://localhost:3001",
  igniteServiceUrl:
    getEnv("VITE_IGNITE_SERVICE_URL") || "http://localhost:3002",
  firebase: {
    apiKey: getEnv("VITE_FIREBASE_API_KEY") || "",
    authDomain: getEnv("VITE_FIREBASE_AUTH_DOMAIN") || "",
    projectId: getEnv("VITE_FIREBASE_PROJECT_ID") || "",
    storageBucket: getEnv("VITE_FIREBASE_STORAGE_BUCKET") || "",
    messagingSenderId: getEnv("VITE_FIREBASE_MESSAGING_SENDER_ID") || "",
    appId: getEnv("VITE_FIREBASE_APP_ID") || "",
  },
};

// Product analytics (PLAT-313): PostHog OSS config. Missing values disable
// the integration gracefully — initAnalytics() in lib/analytics.ts no-ops
// and call sites fall back to Sentry breadcrumbs only. The values are
// resolved at call time via getEnv (not captured here at module load) per
// the env-capture lesson — see feedback_module_level_env_capture.
